/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import {
  ECompanyConfigEnabledFlagListType,
  ECompanySubscriptionType,
  EFeatureListType,
  ELoginConfigType,
  ESubProductCodeType,
  ICompany
} from '@aqx-core/common/api/config';

export const validateCompanyAccess = (company?: ICompany | null) => {
  /**
   *  Start - Backoffice and Membersite features controlled by root admin
   */
  const accessFlags = {
    hasFundManagementAccess: false,

    // Third-party integrations
    hasThirdPartyAccess: false,
    hasNewsAccess: false,
    hasCalendarAccess: false,
    hasCopyTradeAccess: false,
    hasDealerAccess: false,
    hasSignalAccess: false,
    hasEducationAccess: false,
    hasTradingViewChartAccess: false,

    // Account management
    hasAccountClosureAccess: false,
    hasResetPasswordAccess: false, // Ability to reset CRM account password
    hasChangePasswordAccess: false,
    hasForgotPasswordAccess: false, // Ability to reset MT4/MT5 account password
    hasCustomerServiceAccess: false,
    hasPortfolioPieChartAccess: false,

    // Market data features
    hasTopGainerAccess: false,
    hasTopLoserAccess: false,
    hasTopPickAccess: false,
    hasExperienceAccess: false,

    // MEX specific
    hasLadderTrading: false,

    // Login configurations
    hasCompanyLoginConfig: false,
    hasMT4LiveLoginConfig: false,
    hasMT4DemoLoginConfig: false,
    hasMT5LiveLoginConfig: false,
    hasMT5DemoLoginConfig: false,
    hasCentroidLiveLoginConfig: false,
    hasCentroidDemoLoginConfig: false,
    /** End - Backoffice and Membersite features controlled by root admin */

    /** Start - Membersite features controlled by backoffice */
    hasDemoAccountCreation: false,
    hasOneClickTrading: false,
    hasEducationalVideo: false,
    hasSignal: false,
    hasPretradeDetails: false,
    hasEstimatedMarginRequired: false,
    hasTradingViewChart: false,
    hasQuoteboard: false,
    hasCopyTrade: false,

    // Signal access
    hasPremiumSignalAccess: false,
    hasFreemiumSignalAccess: false,

    // Copy trade access
    hasPremiumCopyTradeAccess: false,
    hasFreemiumCopyTradeAccess: false,

    hasPriceAlertAccess: false
    /** End - Membersite features controlled by backoffice */
  };

  if (company) {
    const {
      fundManagement,
      subProductCodeList = [],
      featureList = [],
      loginConfigList = [],
      companyConfigEnabledFlagList = [],
      companySubscriptionList = []
    } = company;

    // Helper functions to set flags
    const hasSubProduct = (code: ESubProductCodeType) => subProductCodeList?.some((c) => c === code) || false;
    const hasFeature = (code: EFeatureListType) => featureList?.some((c) => c === code) || false;
    const hasLoginConfig = (code: ELoginConfigType) => loginConfigList?.some((c) => c === code) || false;
    const hasCompanyConfig = (code: ECompanyConfigEnabledFlagListType) =>
      companyConfigEnabledFlagList?.some((c) => c === code) || false;

    accessFlags.hasFundManagementAccess = !!fundManagement;

    accessFlags.hasNewsAccess = hasSubProduct(ESubProductCodeType.NEWS);
    accessFlags.hasSignalAccess = hasSubProduct(ESubProductCodeType.SIGNAL);
    accessFlags.hasCalendarAccess = hasSubProduct(ESubProductCodeType.CALENDAR);
    accessFlags.hasCopyTradeAccess = hasSubProduct(ESubProductCodeType.COPY_TRADE);
    accessFlags.hasDealerAccess = hasSubProduct(ESubProductCodeType.DEALER);
    accessFlags.hasEducationAccess = hasSubProduct(ESubProductCodeType.EDUCATION);
    accessFlags.hasTradingViewChartAccess = hasSubProduct(ESubProductCodeType.TRADING_VIEW);

    accessFlags.hasAccountClosureAccess = hasFeature(EFeatureListType.ACCOUNT_CLOSURE);
    accessFlags.hasResetPasswordAccess = hasFeature(EFeatureListType.RESET_PASSWORD);
    accessFlags.hasChangePasswordAccess = hasFeature(EFeatureListType.CHANGE_PASSWORD);
    accessFlags.hasCustomerServiceAccess = hasFeature(EFeatureListType.CUSTOMER_SERVICE);
    accessFlags.hasForgotPasswordAccess = hasFeature(EFeatureListType.FORGOT_PASSWORD);
    accessFlags.hasPortfolioPieChartAccess = hasFeature(EFeatureListType.PIE_CHART);

    accessFlags.hasTopGainerAccess = hasFeature(EFeatureListType.TOP_GAINER);
    accessFlags.hasTopLoserAccess = hasFeature(EFeatureListType.TOP_LOSER);
    accessFlags.hasTopPickAccess = hasFeature(EFeatureListType.TOP_PICK);
    accessFlags.hasExperienceAccess = hasFeature(EFeatureListType.EXPERIENCE);

    // MEX specific features
    accessFlags.hasLadderTrading = hasFeature(EFeatureListType.LADDER_TRADING);

    accessFlags.hasCompanyLoginConfig = hasLoginConfig(ELoginConfigType.COMPANY);
    accessFlags.hasMT4LiveLoginConfig = hasLoginConfig(ELoginConfigType.METATRADER4_LIVE);
    accessFlags.hasMT4DemoLoginConfig = hasLoginConfig(ELoginConfigType.METATRADER4_DEMO);
    accessFlags.hasMT5LiveLoginConfig = hasLoginConfig(ELoginConfigType.METATRADER5_LIVE);
    accessFlags.hasMT5DemoLoginConfig = hasLoginConfig(ELoginConfigType.METATRADER5_DEMO);
    accessFlags.hasCentroidLiveLoginConfig = hasLoginConfig(ELoginConfigType.CENTROID_LIVE);
    accessFlags.hasCentroidDemoLoginConfig = hasLoginConfig(ELoginConfigType.CENTROID_DEMO);

    const signalSubscription = companySubscriptionList?.find((item) => item.productCode === ESubProductCodeType.SIGNAL);
    accessFlags.hasPremiumSignalAccess = signalSubscription?.productSubscription === ECompanySubscriptionType.PREMIUM;
    accessFlags.hasFreemiumSignalAccess = accessFlags.hasSignalAccess && !accessFlags.hasPremiumSignalAccess;

    const copyTradeSubscription = companySubscriptionList?.find(
      (item) => item.productCode === ESubProductCodeType.COPY_TRADE
    );
    accessFlags.hasPremiumCopyTradeAccess =
      copyTradeSubscription?.productSubscription === ECompanySubscriptionType.PREMIUM;
    accessFlags.hasFreemiumCopyTradeAccess = accessFlags.hasCopyTradeAccess && !accessFlags.hasPremiumCopyTradeAccess;

    accessFlags.hasThirdPartyAccess =
      accessFlags.hasNewsAccess || accessFlags.hasSignalAccess || accessFlags.hasCalendarAccess;

    accessFlags.hasDemoAccountCreation = hasCompanyConfig(ECompanyConfigEnabledFlagListType.DEMO_ACCOUNT_CREATION);
    accessFlags.hasOneClickTrading = hasCompanyConfig(ECompanyConfigEnabledFlagListType.ONE_CLICK_TRADING);

    accessFlags.hasEducationalVideo = accessFlags.hasEducationAccess
      ? hasCompanyConfig(ECompanyConfigEnabledFlagListType.EDUCATIONAL_VIDEO)
      : false;

    accessFlags.hasSignal = accessFlags.hasSignalAccess
      ? hasCompanyConfig(ECompanyConfigEnabledFlagListType.SIGNAL)
      : false;

    accessFlags.hasPretradeDetails = hasCompanyConfig(ECompanyConfigEnabledFlagListType.PRETRADE_DETAILS);

    accessFlags.hasEstimatedMarginRequired = hasCompanyConfig(
      ECompanyConfigEnabledFlagListType.ESTIMATED_MARGIN_REQUIRED
    );

    accessFlags.hasTradingViewChart = hasCompanyConfig(ECompanyConfigEnabledFlagListType.TRADING_VIEW);

    accessFlags.hasQuoteboard = hasCompanyConfig(ECompanyConfigEnabledFlagListType.QUOTE_BOARD);

    accessFlags.hasCopyTrade = accessFlags.hasCopyTradeAccess
      ? hasCompanyConfig(ECompanyConfigEnabledFlagListType.COPY_TRADE)
      : false;

    accessFlags.hasPriceAlertAccess = hasCompanyConfig(ECompanyConfigEnabledFlagListType.PRICE_ALERT);
  }

  return accessFlags;
};
